/**
 * Pure client-side query layer for the Scan Results Dashboard.
 *
 * This module holds the deterministic, side-effect-free functions the SPA uses
 * to derive the displayed repository list from the loaded `RepoSummary[]`:
 * free-text search (Req 4), attribute/capability filtering (Req 5, 6), sorting
 * (Req 7), and their composition (`applyQuery`). Each function is pure — it
 * never mutates its inputs and returns a new array — so it can be re-run on
 * every keystroke within budget (Req 12.1, 12.2) and exhaustively
 * property-tested.
 *
 * Functions are added here incrementally by the spec tasks; this file currently
 * implements `searchSummaries` (task 6.1). `filterSummaries`, `sortSummaries`,
 * and `applyQuery` follow in later tasks.
 */

import type {
  CapabilityKey,
  DashboardLibraryIndex,
  FilterState,
  LibraryRepoEntry,
  QueryState,
  RepoSummary,
  SortColumn,
  SortState,
} from "../types";

// ---------------------------------------------------------------------------
// Search (Req 4)
// ---------------------------------------------------------------------------

/**
 * Free-text search over a summary list (Req 4.1–4.5; design Property 6).
 *
 * The term is trimmed of leading/trailing whitespace and lower-cased; a term
 * that is empty or whitespace-only is treated as no search and returns every
 * item unchanged (Req 4.3, 4.5). Otherwise a summary is kept when the
 * normalized term appears as a case-insensitive substring of any of its
 * searchable fields (Req 4.1, 4.2):
 *
 *   - `repoName`
 *   - `description`
 *   - any single entry in `topics`
 *   - `primaryLanguage`
 *
 * `description` and `primaryLanguage` may be `null`/absent; such fields are
 * simply treated as non-matches and never raise (Req 4.4). The input array is
 * not mutated; relative order is preserved.
 */
export function searchSummaries(
  items: RepoSummary[],
  term: string,
): RepoSummary[] {
  const normalized = term.trim().toLowerCase();

  // Empty or whitespace-only term: search excludes nothing (Req 4.3, 4.5).
  if (normalized === "") {
    return items.slice();
  }

  return items.filter((item) => summaryMatches(item, normalized));
}

/**
 * True when `normalizedTerm` (already trimmed + lower-cased and known
 * non-empty) appears as a substring of any of the summary's searchable fields.
 * Null/absent candidate fields contribute no match and never throw (Req 4.4).
 */
function summaryMatches(item: RepoSummary, normalizedTerm: string): boolean {
  if (fieldMatches(item.repoName, normalizedTerm)) return true;
  if (fieldMatches(item.description, normalizedTerm)) return true;
  if (fieldMatches(item.primaryLanguage, normalizedTerm)) return true;

  for (const topic of item.topics) {
    if (fieldMatches(topic, normalizedTerm)) return true;
  }

  return false;
}

/**
 * Case-insensitive substring test for a single candidate field. A `null` or
 * `undefined` field is a non-match (Req 4.4).
 */
function fieldMatches(
  value: string | null | undefined,
  normalizedTerm: string,
): boolean {
  if (value == null) return false;
  return value.toLowerCase().includes(normalizedTerm);
}

// ---------------------------------------------------------------------------
// Filter (Req 5, Req 6)
// ---------------------------------------------------------------------------

/**
 * Attribute/capability filtering over a summary list (Req 5.1-5.8, 6.1-6.10;
 * design Property 8 and Property 9).
 *
 * A dimension is *active* only when it carries a selection: an attribute
 * dimension (`status`, `language`, `visibility`, `private`, `archived`) is
 * inactive when its array is empty, and a capability dimension is inactive when
 * its key is absent from `filters.capabilities` (Req 5.10, 6.10). An empty
 * filter state therefore returns every item unchanged.
 *
 * Within a single active dimension the selected values are OR-ed (Req 5.6);
 * across active dimensions the per-dimension results are AND-ed — a summary is
 * kept only when it satisfies every active dimension (Req 5.7, 6.10).
 *
 * For attribute dimensions, a summary whose value is `null` is excluded from an
 * active dimension regardless of which values are selected (Req 5.8); equality
 * against the selected values is exact (Req 5.1-5.5).
 *
 * For capability dimensions the eight `hasX` booleans on `RepoSummary` are
 * always defined: `present` mode keeps `hasX === true` (Req 6.1-6.8), and
 * `absent` mode keeps `hasX === false`, the exact complement (Req 6.9), so the
 * two modes partition the list (Property 9).
 *
 * The input array is not mutated; relative order is preserved.
 */
export function filterSummaries(
  items: RepoSummary[],
  filters: FilterState,
): RepoSummary[] {
  return items.filter((item) => summaryPassesFilters(item, filters));
}

/**
 * True when `item` satisfies every active filter dimension (AND across
 * dimensions). Inactive dimensions impose no constraint.
 */
function summaryPassesFilters(item: RepoSummary, filters: FilterState): boolean {
  // Attribute dimensions: empty selection is inactive; null value excludes;
  // OR within the dimension via exact-equality membership (Req 5.1-5.8).
  if (!attributeDimensionPasses(item.status, filters.status)) return false;
  if (!attributeDimensionPasses(item.primaryLanguage, filters.language)) {
    return false;
  }
  if (!attributeDimensionPasses(item.visibility, filters.visibility)) {
    return false;
  }
  if (!attributeDimensionPasses(item.private, filters.private)) return false;
  if (!attributeDimensionPasses(item.archived, filters.archived)) return false;

  // Capability dimensions: absent key is inactive; present/absent select the
  // matching boolean value (Req 6.1-6.10).
  for (const key of Object.keys(filters.capabilities) as CapabilityKey[]) {
    const mode = filters.capabilities[key];
    if (mode === undefined) continue; // inactive (Req 6.10)
    const want = mode === "present"; // present -> true, absent -> false (Req 6.9)
    if (item[key] !== want) return false;
  }

  return true;
}

/**
 * Membership test for one active attribute dimension. An empty `selected`
 * array means the dimension is inactive and imposes no constraint (Req 5.10).
 * A `null` summary value is excluded from any active dimension (Req 5.8);
 * otherwise the value must exactly equal one of the selected values (OR within
 * the dimension — Req 5.6).
 */
function attributeDimensionPasses<T>(
  value: T | null,
  selected: T[],
): boolean {
  if (selected.length === 0) return true; // inactive dimension
  if (value === null) return false; // null excluded (Req 5.8)
  return selected.includes(value);
}

// ---------------------------------------------------------------------------
// Sort (Req 7)
// ---------------------------------------------------------------------------

/**
 * Sort a summary list by the active column and direction (Req 7.1-7.10;
 * design Property 12).
 *
 * The result is a brand-new array — the input is never mutated and a shallow
 * copy is sorted, so callers keep their original ordering. The ordering is a
 * correct, total, and stable order:
 *
 *   - Each column uses its natural comparator: case-insensitive lexicographic
 *     for `repoName`, `primaryLanguage`, and `status` (Req 7.1, 7.2, 7.5);
 *     numeric for `size` (Req 7.3); chronological (by parsed instant) for the
 *     ISO-8601 `pushedAt` (Req 7.4).
 *   - Summaries whose value for the active column is `null` always sort *after*
 *     every summary that has a value, in both ascending and descending
 *     directions (Req 7.8). Only `primaryLanguage`, `size`, and `pushedAt` are
 *     nullable; `repoName` and `status` are always present.
 *   - Ties on the column are broken case-insensitively lexicographically by
 *     `repoName` (Req 7.9), giving a total order (hence stable).
 *   - Descending order is the ascending comparator reversed, *except* that the
 *     nulls-last rule is applied independent of direction so nulls remain last
 *     (Req 7.8; design Property 12).
 *   - When no column is selected (`sort.column === null`), the default is
 *     ascending by `repoName` (Req 7.10).
 */
export function sortSummaries(
  items: RepoSummary[],
  sort: SortState,
): RepoSummary[] {
  // Default (no column selected) is ascending by repoName (Req 7.10). repoName
  // is never null, so the comparator naturally reduces to the tie-break.
  const column: SortColumn = sort.column ?? "repoName";
  const sign = sort.column !== null && sort.direction === "desc" ? -1 : 1;

  // Sort a shallow copy so the caller's array is never mutated (pure).
  return items.slice().sort((a, b) => {
    const av = sortValue(a, column);
    const bv = sortValue(b, column);

    // Nulls always sort last, in both directions (Req 7.8) — applied before the
    // direction sign so it is never reversed.
    const aNull = av === null;
    const bNull = bv === null;
    if (aNull && bNull) {
      // Tie on the column: fall through to the tie-break (reversed for desc).
      return sign * tieBreakByRepoName(a, b);
    }
    if (aNull) return 1; // a after b
    if (bNull) return -1; // b after a

    const cmp = compareSortValues(column, av, bv);
    if (cmp !== 0) return sign * cmp;

    // Equal on the column: break the tie by case-insensitive repoName (Req 7.9).
    return sign * tieBreakByRepoName(a, b);
  });
}

/** The raw, possibly-null value a summary contributes for a given sort column. */
function sortValue(
  item: RepoSummary,
  column: SortColumn,
): string | number | null {
  switch (column) {
    case "repoName":
      return item.repoName;
    case "primaryLanguage":
      return item.primaryLanguage;
    case "size":
      return item.size;
    case "pushedAt":
      return item.pushedAt;
    case "status":
      return item.status;
  }
}

/**
 * Compare two non-null column values using the column's natural comparator:
 * numeric for `size`, chronological for `pushedAt`, and case-insensitive
 * lexicographic for the string columns (Req 7.1-7.5).
 */
function compareSortValues(
  column: SortColumn,
  a: string | number,
  b: string | number,
): number {
  if (column === "size") {
    // Numeric (Req 7.3).
    return (a as number) - (b as number);
  }
  if (column === "pushedAt") {
    // Chronological by parsed instant so timezone offsets compare correctly
    // (Req 7.4).
    return Date.parse(a as string) - Date.parse(b as string);
  }
  // repoName / primaryLanguage / status: case-insensitive lexicographic
  // (Req 7.1, 7.2, 7.5).
  return compareCaseInsensitive(a as string, b as string);
}

/** Case-insensitive tie-break by `repoName`, which is always present (Req 7.9). */
function tieBreakByRepoName(a: RepoSummary, b: RepoSummary): number {
  return compareCaseInsensitive(a.repoName, b.repoName);
}

/** Case-insensitive lexicographic comparison by lower-cased code points. */
function compareCaseInsensitive(a: string, b: string): number {
  const la = a.toLowerCase();
  const lb = b.toLowerCase();
  if (la < lb) return -1;
  if (la > lb) return 1;
  return 0;
}

// ---------------------------------------------------------------------------
// Composition (Req 4.6, 5.9, 5.10, 6.10, 7.7)
// ---------------------------------------------------------------------------

/**
 * Apply a complete `QueryState` to a summary list (Req 4.6, 5.9, 5.10, 6.10,
 * 7.7; design Property 14).
 *
 * The three stages compose in a fixed order — filter, then search, then sort:
 *
 *   sortSummaries(searchSummaries(filterSummaries(items, filters), term), sort)
 *
 * Filtering and searching commute (both are order-preserving predicates), and
 * sorting is applied last so the displayed ordering reflects the final visible
 * set (Req 7.7). The function is pure and deterministic: it delegates to the
 * three pure stage functions, never mutates `items` (each stage returns a fresh
 * array), and depends only on its arguments, so identical inputs always yield
 * an identical result and it is safe to re-run on every keystroke (Req 12).
 */
export function applyQuery(
  items: RepoSummary[],
  query: QueryState,
): RepoSummary[] {
  return sortSummaries(
    searchSummaries(filterSummaries(items, query.filters), query.searchTerm),
    query.sort,
  );
}

// ---------------------------------------------------------------------------
// Repo Knowledge Library join (Req 18.2, 18.6) — ADDITIVE
// ---------------------------------------------------------------------------
//
// These pure helpers join the per-run `DashboardLibraryIndex` (the camelCase
// Library document at `library/run-date=.../run-id=.../dashboard-index.json`)
// to the existing `RepoSummary` rows by `repoName` + `scanId` (Req 18.2). They
// are deterministic and side-effect-free: they never mutate their inputs and
// tolerate a `null` index (an absent Library document) so older runs that have
// no Library artifact join to nothing and render exactly as today. They do not
// touch the search/filter/sort pipeline above — the Library is purely additive
// per-row enrichment the view can overlay when present.

/**
 * Composite join key for a Library entry / summary row: `repoName` and `scanId`
 * combined with a NUL (`\u0000`) separator. NUL cannot occur in a repo name or
 * scan id, so the concatenation is unambiguous — distinct `(repoName, scanId)`
 * pairs always map to distinct keys and identical pairs always collide. The
 * same separator convention is used for the selection key in `useRunLoader`.
 */
export function libraryJoinKey(repoName: string, scanId: string): string {
  return `${repoName}\u0000${scanId}`;
}

/**
 * Index a `DashboardLibraryIndex` by its composite `libraryJoinKey` for O(1)
 * per-row lookup during the join (Req 18.2). A `null` index — the graceful
 * absence case where no Library document exists for the run — yields an empty
 * map, so every summary joins to nothing and the view degrades to today's
 * behavior. The input is not mutated; when two entries share a key (the
 * document is expected to carry at most one entry per `(repoName, scanId)`) the
 * last one wins, matching `Map` insertion semantics.
 */
export function indexLibraryEntries(
  index: DashboardLibraryIndex | null,
): Map<string, LibraryRepoEntry> {
  const byKey = new Map<string, LibraryRepoEntry>();
  if (index === null) {
    return byKey;
  }
  for (const entry of index.repos) {
    byKey.set(libraryJoinKey(entry.repoName, entry.scanId), entry);
  }
  return byKey;
}

/**
 * Resolve the `LibraryRepoEntry` whose `repoName` + `scanId` match `summary`,
 * using a map produced by {@link indexLibraryEntries} (Req 18.2). Returns the
 * matching entry, or `null` when the summary has no Library counterpart (an
 * older run with no Library document, or a repo absent from it). Pure: it reads
 * only `summary.repoName` / `summary.scanId` and never mutates the map.
 */
export function joinLibraryEntry(
  summary: RepoSummary,
  byKey: Map<string, LibraryRepoEntry>,
): LibraryRepoEntry | null {
  return byKey.get(libraryJoinKey(summary.repoName, summary.scanId)) ?? null;
}
